import type { BookAuthor, Reviewer } from '../common/types';

export interface AssignedManuscript {
    id: string;
    authorName: string;
    author?: string;
    title: string;
    status: 'Pending' | 'Reviewed';
    dateAssigned: string;
    thematicArea: string;
    reviewDeadline: string;
    plagiarismScore: number;
    abstract?: string;
}

export interface EvaluationSection {
    id: string;
    name: string;
    maxScore: number;
    score: number;
    weight: string;
    content: string;
}

export interface ReviewEvaluationData {
    manuscriptId: string;
    sections: EvaluationSection[];
    overallComment: string;
    reviewerRemark?: string;
    recommendation: string;
    attachmentUrl?: string;
}

export interface AssessmentReviewer {
    reviewerId: string;
    reviewerName: string;
    assignedAt: string;
    isSubmitted: boolean;
    submittedAt: string | null;
    score: number | null;
    remarks: string | null;
    reviewAssignmentId: string;
}

export interface SubmissionReviewProgress {
    submissionId: string;
    submissionTitle: string;
    submissionNumber: string;
    category: string;
    applicationType: string;
    bookAuthors: BookAuthor[];
    reviewer: AssessmentReviewer | null;
    totalScore: number | null;
}

export interface SubmissionWithReviewer {
    submissionId: string;
    submissionNumber: string;
    title: string;
    geoPoliticalZone: string;
    status: string;
    submittedAt: string;
    fullName: string;
    authors: string;
    categoryName: string;
    categoryId: number;
    averageScore: number;
    threshold: number;
    bookAuthors: BookAuthor[];
    reviewers: Reviewer[];
    applicationType: number;
}

export interface GetSubmissionsPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search?: string;
    categoryId?: number | null;
    cycleId?: number | null;
    authorId?: string;
    submittedFrom?: string | null;
    submittedTo?: string | null;
    applicationType?: number | null;
    geoPoliticalZoneId?: number | null;
}

export interface SearchSubmissionPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search: string;
}

export enum SubmissionStatus {
    Submitted   = 1,
    UnderReview = 2,
    Returned    = 3,
    Rejected    = 4,
    Approved    = 5,
    SubmitteRevisedCopy = 6,
    Resubmit    = 7,
}

// Final-decision values that allow the "Move submission to Author" action.
// Anything else (ReturnToReviewer, AcceptableForFunding, NotAcceptableForFunding,
// etc.) keeps the action hidden because it isn't directed at the author.
export type AuthorMoveDecision =
    | 'AcceptableForPublication'
    | 'NotAcceptableForPublication'
    | 'ReturnToAuthorForRevisedCopy';

export const isAuthorMoveDecision = (d?: string | null): d is AuthorMoveDecision =>
    d === 'AcceptableForPublication' ||
    d === 'NotAcceptableForPublication' ||
    d === 'ReturnToAuthorForRevisedCopy';

export const statusForAuthorMoveDecision = (
    d: AuthorMoveDecision
): { value: SubmissionStatus; label: string } => {
    if (d === 'AcceptableForPublication') return { value: SubmissionStatus.Approved, label: 'Approved' };
    if (d === 'NotAcceptableForPublication') return { value: SubmissionStatus.Rejected, label: 'Rejected' };
    return { value: SubmissionStatus.SubmitteRevisedCopy, label: 'Submit Revised Copy' };
};

export interface GetAllAdminSubmissionsPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search?: string;
    categoryId?: number;
    submissionStatus?: number;
    currentStage?: number;
}

// Mirrors the backend WorkflowStage enum.
export enum WorkflowStage {
    ESRegistry           = 1,
    ES                   = 2,
    DESS                 = 3,
    TAGDivisionalHead    = 4,
    TAGChairman          = 5,
    BookHead             = 6,
    JournalHead          = 7,
    ThematicAreaLeader   = 8,
    ThematicAreaMembers  = 9,
    Reviewer             = 10,
    ESSStaff             = 11,
    Author               = 12,
}

export const WORKFLOW_STAGE_LABELS: Record<number, string> = {
    [WorkflowStage.ESRegistry]:          'ES Registry',
    [WorkflowStage.ES]:                  'ES',
    [WorkflowStage.DESS]:                'DESS',
    [WorkflowStage.TAGDivisionalHead]:   'TAG Divisional Head',
    [WorkflowStage.TAGChairman]:         'TAG Chairman',
    [WorkflowStage.BookHead]:            'Book Convener',
    [WorkflowStage.JournalHead]:         'Journal Convener',
    [WorkflowStage.ThematicAreaLeader]:  'Thematic Area Leader',
    [WorkflowStage.ThematicAreaMembers]: 'Thematic Area Members',
    [WorkflowStage.Reviewer]:            'Reviewer',
    [WorkflowStage.ESSStaff]:            'ESS Staff',
    [WorkflowStage.Author]:              'Author',
};

export interface GetSubmissionsByDeskPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search?: string;
    categoryId?: number;
}

export interface MoveSubmissionPayload {
    submissionId: string;
    comment: string;
    toWorkflowStage: number;
}

export interface CommunicateSubmissionStatusToAuthorPayload {
    submissionId: string;
    comment: string;
    submissionStatus: number;
}

export interface MoveSubmissionToAuthorPayload {
    submissionId: string;
    comment: string;
    submissionStatus: number;
}

export interface MoveManuscriptReviewToAuthorPayload {
    manuscriptReviewId: string;
    comment: string;
    submissionStatus: number;
}

export interface UploadedVersion {
    id: string;
    manuscriptUrl: string;
    dateSubmitted: string;
    submissionId: string;
    comment: string | null;
    versionNumber: number;
}
